// src/components/Header.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-19
//
//  1. DARK MODE FORGOT ITSELF.
//       const [dark, setDark] = useState(false);
//       The theme lived only in React memory and started as `false` every
//       time this component mounted. Two separate things reset it:
//         (a) every sidebar click, because those were plain <a href> links
//             that reloaded the whole app — fixed in Sidebar.js this pass
//         (b) any real browser refresh
//       (a) is now fixed at the source. (b) is fixed here: the choice is
//       saved and read back on load, so dark mode stays on until YOU turn
//       it off. Every read and write is wrapped in try/catch, so a browser
//       with storage blocked still works — it just forgets, as it does now.
//
//  2. "SETTINGS" IN THE USER DROPDOWN LED TO THE LOGIN FORM.
//       <a href="/settings"> — there is no /settings route, so it hit the
//       catch-all and landed on the login form, looking like a logout.
//       It is now a <Link>, and App.js gives /settings a "Coming Soon"
//       page. No reload, no fake logout.
//
//  NOT CHANGED: the clock, the notification panel, the user dropdown
//  contents, logout, every class name and every style.
//
//  ⚠️ NOTE, not fixed in this pass — the notification bell shows four
//     HARDCODED messages ("Today's revenue crossed $1,000!", "Everflow CVR
//     up 0.4%…"). They are invented, exactly like the old sparklines were,
//     and the badge always reads "4". Left alone for now because removing
//     the bell is a design decision, not a bug fix. Raised with Aditi
//     2026-09-19.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";          // ADDED 2026-09-19
import { useAuth } from "../context/AuthContext";
import { edtHeaderStamp } from "../utils/edt";
import "../styles/header.css";

// ── ADDED 2026-09-19 ──────────────────────────────────────────────────────
// Where the theme choice is remembered. Same naming convention as the login
// token (`ipanel_token`) so everything this app stores is easy to spot.
const THEME_KEY = "ipanel_theme";

// Read the saved theme. Returns false (light) if nothing is saved, or if the
// browser refuses to let us read storage — private windows and some locked
// down corporate profiles do exactly that, and throwing here would blank the
// whole header.
function readSavedTheme() {
  try {
    return localStorage.getItem(THEME_KEY) === "dark";
  } catch (e) {
    return false;
  }
}
// ──────────────────────────────────────────────────────────────────────────

export default function Header({ title, subtitle }) {
  const { user, logout } = useAuth();
  // CHANGED 2026-09-19: was useState(false) — always light on every mount.
  // The function form runs once, on first render, so we read storage once
  // rather than on every re-render.
  const [dark,         setDark]         = useState(readSavedTheme);
  const [notifOpen,    setNotifOpen]    = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);

  // ── Live EDT clock ─────────────────────────────────────────
  // Shows e.g. "Sat, Apr 18, 2026 • 10:45 AM EDT"
  // Re-computes every 30 seconds so the minute always matches EDT.
  const [stamp, setStamp] = useState(() => edtHeaderStamp());
  useEffect(() => {
    const tick = () => setStamp(edtHeaderStamp());
    tick(); // refresh immediately on mount
    const id = setInterval(tick, 30 * 1000);  // 30s is plenty for a minute-precision clock
    return () => clearInterval(id);
  }, []);

  // ⚠️ These four are hardcoded placeholders, not real notifications.
  // See the note at the top of this file.
  const notifications = [
    { icon:"🏆", text:"Today's revenue crossed $1,000!",   time:"Just now", hi:true  },
    { icon:"📈", text:"Everflow CVR up 0.4% vs yesterday", time:"1h ago",   hi:false },
    { icon:"⚠️", text:"Domain expiring in 7 days",          time:"3h ago",   hi:false },
    { icon:"✅", text:"New offer approved on Instar",        time:"5h ago",   hi:false },
  ];

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    // ── ADDED 2026-09-19 ──────────────────────────────────────────────
    // Remember the choice. Wrapped because storage can be unavailable or
    // full; if it fails the app carries on exactly as it does today — it
    // just won't remember next time.
    try {
      localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch (e) { /* storage blocked or full — not worth breaking the page */ }
  }, [dark]);

  const userName = user?.name || "Admin";

  return (
    <div className="ph">
      {/* Title + optional subtitle on same line */}
      <div className="ph-title-wrap" style={{display:"flex",alignItems:"baseline",gap:14}}>
        <div className="ph-title">{title}</div>
        {subtitle && (
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--text-muted)",fontWeight:400}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"#22C55E",display:"inline-block",flexShrink:0}}/>
            <span>{subtitle}</span>
          </div>
        )}
      </div>

      <div className="ph-r">
        {/* EDT date + time — always EDT, never IST */}
        <div className="ph-date" title="All times shown in US Eastern time (EDT/EST)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8"  y1="2" x2="8"  y2="6"/>
            <line x1="3"  y1="10" x2="21" y2="10"/>
          </svg>
          {stamp}
        </div>

        {/* Dark/Light toggle — the choice is now remembered (see top of file) */}
        <button className={`ph-toggle ${dark?"on":""}`} onClick={() => setDark(!dark)}
          title={dark ? "Switch to light mode" : "Switch to dark mode"}>
          {dark
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          }
        </button>

        {/* Notification Bell */}
        <div className="ph-nwrap">
          <button className="ph-nbtn" onClick={() => { setNotifOpen(!notifOpen); setUserDropOpen(false); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span className="nb">4</span>
          </button>
          {notifOpen && <>
            <div className="nd-ov" onClick={() => setNotifOpen(false)}/>
            <div className="nd">
              <div className="nd-hd">
                <span className="nd-ttl">Notifications</span>
                <span className="nd-cnt">4 new</span>
              </div>
              {notifications.map((n, i) => (
                <div key={i} className={`nd-item ${n.hi?"hi":""}`}>
                  <span className="nd-ico">{n.icon}</span>
                  <div><div className="nd-txt">{n.text}</div><div className="nd-tm">{n.time}</div></div>
                </div>
              ))}
              <div className="nd-ft">Mark all as read</div>
            </div>
          </>}
        </div>

        {/* User dropdown */}
        <div className="ph-uwrap">
          <button className="ph-user" onClick={() => { setUserDropOpen(!userDropOpen); setNotifOpen(false); }}>
            <div className="ph-av">{userName.charAt(0).toUpperCase()}</div>
            <div>
              <div className="ph-name">{userName}</div>
              <div className="ph-role">{user?.role || "Admin"}</div>
            </div>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{transition:"transform 0.2s", transform:userDropOpen?"rotate(180deg)":"rotate(0deg)"}}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {userDropOpen && <>
            <div className="nd-ov" onClick={() => setUserDropOpen(false)}/>
            <div className="user-drop">
              <div className="udrop-info">
                <div className="ph-av" style={{width:36,height:36,fontSize:15}}>
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>{userName}</div>
                  <div style={{fontSize:11,color:"#D4A017",textTransform:"capitalize"}}>{user?.role}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)"}}>{user?.email}</div>
                </div>
              </div>
              <div className="udrop-divider"/>
              {/* CHANGED 2026-09-19: was <a href="/settings"> — no such route
                  existed, so this dropped you on the login form. Now a
                  <Link>, and App.js routes /settings to a Coming Soon page. */}
              <Link to="/settings" className="udrop-item" onClick={() => setUserDropOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                Settings
              </Link>
              <div className="udrop-divider"/>
              <button className="udrop-item udrop-logout" onClick={() => { setUserDropOpen(false); logout(); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Logout
              </button>
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}
